import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Text, Card, SimpleGrid, Skeleton } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import type { DiscoverResult, DiscoverUserDto } from '../../app/models/discover'
import EmptyState from '../../app/common/ui/EmptyState'
import AmmoScoreBadge from '../../app/common/ui/AmmoScoreBadge'
import AuthGateCta from '../../app/common/ui/AuthGateCta'

// ─── Tier colours (mirrors AmmoScoreBadge) ───────────────────────────────────

const TIER_BORDER: Record<string, string> = {
  Newcomer: '#8B949E',
  Enthusiast: '#58A6FF',
  Contributor: '#3FB950',
  Expert: '#E85D04',
  Curator: '#C9A84C',
  Armorer: '#C9A84C'
}

const TOP_TIERS = new Set(['Armorer', 'Curator'])

function getInitials (displayName: string | null | undefined, username: string): string {
  const name = (displayName || username || '?').trim()
  const parts = name.split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

// ─── UserCard ────────────────────────────────────────────────────────────────

interface UserCardProps {
  user: DiscoverUserDto;
  isFollowing: boolean;
  onFollowToggle: (username: string) => void;
  onCardClick: (username: string) => void;
  isLoggedIn: boolean;
}

function UserCard ({ user, isFollowing, onFollowToggle, onCardClick, isLoggedIn }: UserCardProps) {
  const borderColor = TIER_BORDER[user.ammoTier] ?? '#8B949E'
  const isTopTier = TOP_TIERS.has(user.ammoTier)

  return (
    <Box
      sx={{
        background: 'var(--gs-surface2)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }}
    >
      {/* Avatar + info — clickable */}
      <Box
        onClick={() => onCardClick(user.username)}
        sx={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
      >
        {/* Avatar */}
        {user.mainPhotoUrl
          ? (
          <img
            src={user.mainPhotoUrl}
            alt={user.displayName}
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
              border: `2px solid ${borderColor}`
            }}
          />
            )
          : (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              flexShrink: 0,
              background: isTopTier
                ? 'linear-gradient(135deg, var(--gs-accent), var(--gs-gold, #C9A84C))'
                : 'var(--gs-surface)',
              border: `2px solid ${borderColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 700,
              color: isTopTier ? '#fff' : 'var(--gs-text)',
              fontFamily: 'Oswald, sans-serif'
            }}
          >
            {getInitials(user.displayName, user.username)}
          </Box>
            )}

        {/* Name + badge */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--gs-text)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {user.displayName || user.username}
          </Text>
          <Text
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 11,
              color: 'var(--gs-muted)',
              marginBottom: 4
            }}
          >
            @{user.username}
          </Text>
          <AmmoScoreBadge tier={user.ammoTier} score={user.ammoScore} size="sm" />
        </Box>
      </Box>

      {/* Follow button */}
      {isLoggedIn && (
        <button
          onClick={() => onFollowToggle(user.username)}
          style={{
            width: '100%',
            padding: '6px 0',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'DM Sans, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.15s',
            ...(isFollowing
              ? {
                  background: 'var(--gs-surface2)',
                  color: 'var(--gs-muted)',
                  border: '1.5px solid var(--gs-border-bright)'
                }
              : {
                  background: 'var(--gs-accent)',
                  color: '#fff',
                  border: 'none'
                })
          }}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </button>
      )}
    </Box>
  )
}

// ─── Section skeleton ────────────────────────────────────────────────────────

function SectionSkeletons () {
  return (
    <SimpleGrid
      cols={3}
      breakpoints={[
        { maxWidth: 992, cols: 2 },
        { maxWidth: 640, cols: 1 }
      ]}
      spacing="sm"
    >
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} height={130} radius={8} />
      ))}
    </SimpleGrid>
  )
}

// ─── Section ─────────────────────────────────────────────────────────────────

interface SectionProps {
  label: string;
  users: DiscoverUserDto[];
  followState: Record<string, boolean>;
  onFollowToggle: (username: string) => void;
  onCardClick: (username: string) => void;
  loading: boolean;
  isLoggedIn: boolean;
}

function Section ({
  label,
  users,
  followState,
  onFollowToggle,
  onCardClick,
  loading,
  isLoggedIn
}: SectionProps) {
  return (
    <Card
      sx={{
        background: 'var(--gs-surface)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}
    >
      <Text
        sx={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          color: 'var(--gs-muted)',
          marginBottom: 12,
          fontWeight: 600
        }}
      >
        {label}
      </Text>

      {loading
        ? (
        <SectionSkeletons />
          )
        : users.length === 0
          ? (
        <EmptyState
          title="You know everyone here"
          message="Check back soon for new members."
        />
            )
          : (
        <SimpleGrid
          cols={3}
          breakpoints={[
            { maxWidth: 992, cols: 2 },
            { maxWidth: 640, cols: 1 }
          ]}
          spacing="sm"
        >
          {users.map((u) => (
            <UserCard
              key={u.username}
              user={u}
              isFollowing={followState[u.username] ?? u.isFollowing}
              onFollowToggle={onFollowToggle}
              onCardClick={onCardClick}
              isLoggedIn={isLoggedIn}
            />
          ))}
        </SimpleGrid>
            )}
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default observer(function DiscoverPage () {
  const history = useHistory()
  const { userStore } = useStore()

  const [results, setResults] = useState<DiscoverResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [followState, setFollowState] = useState<Record<string, boolean>>({})

  useEffect(() => {
    agent.Profiles.discover()
      .then((data) => {
        setResults(data)
        // Seed local follow state from API response
        const fs: Record<string, boolean> = {};
        [...data.topContributors, ...data.recentlyActive, ...data.newMembers].forEach((u) => {
          fs[u.username] = u.isFollowing
        })
        setFollowState(fs)
      })
      .catch(() => setResults({ topContributors: [], recentlyActive: [], newMembers: [] }))
      .finally(() => setLoading(false))
  }, [])

  const handleFollowToggle = async (username: string) => {
    const currently = followState[username] ?? false
    setFollowState((prev) => ({ ...prev, [username]: !currently }))
    try {
      if (currently) {
        await agent.Profiles.unfollow(username)
      } else {
        await agent.Profiles.follow(username)
      }
    } catch {
      // Revert optimistic update on failure
      setFollowState((prev) => ({ ...prev, [username]: currently }))
    }
  }

  const handleCardClick = (username: string) => {
    history.push(`/profiles/${username}`)
  }

  return (
    <Box sx={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 48px' }}>
      {/* Page header */}
      <Box sx={{ marginBottom: 24 }}>
        <Text
          sx={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.2
          }}
        >
          DISCOVER PEOPLE
        </Text>
        <Text
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            color: 'var(--gs-muted)',
            marginTop: 4
          }}
        >
          Find firearms enthusiasts worth following.
        </Text>
      </Box>

      {/* Conversion banner for anon visitors */}
      {!userStore.isLoggedIn && (
        <AuthGateCta
          variant="banner"
          action="follow these members and build your community"
          pitch="Create a free account to follow collectors, share your builds, and earn your place on the leaderboard."
        />
      )}

      <Section
        label="Top Contributors"
        users={results?.topContributors ?? []}
        followState={followState}
        onFollowToggle={handleFollowToggle}
        onCardClick={handleCardClick}
        loading={loading}
        isLoggedIn={userStore.isLoggedIn}
      />

      <Section
        label="Recently Active"
        users={results?.recentlyActive ?? []}
        followState={followState}
        onFollowToggle={handleFollowToggle}
        onCardClick={handleCardClick}
        loading={loading}
        isLoggedIn={userStore.isLoggedIn}
      />

      <Section
        label="New Members"
        users={results?.newMembers ?? []}
        followState={followState}
        onFollowToggle={handleFollowToggle}
        onCardClick={handleCardClick}
        loading={loading}
        isLoggedIn={userStore.isLoggedIn}
      />
    </Box>
  )
})
